import {
  CheckCircleOutlined,
  DescriptionOutlined,
  PeopleOutline,
  ScheduleOutlined,
  WarningAmberOutlined,
  BarChartOutlined,
  AssessmentOutlined,
  EditOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { CardContainer, MetricCard, Tag, Button } from 'finara-components';
import React from 'react';

import { Navbar } from '@/components/admin/general/Navbar';
import {
  complianceMetrics,
  pendingActions,
  companyPolicies,
  certificationStatuses,
  upcomingTrainings,
  quickActions,
} from '@/data/admin/compliance/complianceData';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { useAuth } from '@/hooks/useAuth';

export const CompliancePage: React.FC = () => {
  const { user } = useAuth();

  const getPriorityVariant = (priority: string): 'red' | 'yellow' => {
    return priority === 'Alta' ? 'red' : 'yellow';
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'description':
        return <DescriptionOutlined />;
      case 'edit':
        return <EditOutlined />;
      case 'schedule':
        return <ScheduleOutlined />;
      default:
        return <DescriptionOutlined />;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar
        navItems={adminNavItems}
        userName={user?.name || 'Usuario'}
        pageTitle="Cumplimiento Empresarial"
        section="Cumplimiento"
      />

      <div className="px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AssessmentOutlined />
            Cumplimiento Empresarial
          </h1>
          <p className="text-gray-600 mt-1">
            Centro de cumplimiento específico de tu empresa con políticas internas, documentación
            regulatoria y gestión de compliance requerida por Finara.
          </p>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-1 md:!grid-cols-2 lg:!grid-cols-4 gap-6 mb-6">
          <MetricCard
            title="Tasa de Cumplimiento"
            value={`${complianceMetrics.complianceRate}%`}
            icon={<CheckCircleOutlined className="text-green-600" />}
            variant="white"
          />
          <MetricCard
            title="Políticas Actualizadas"
            value={complianceMetrics.updatedPolicies}
            icon={<DescriptionOutlined className="text-blue-600" />}
            variant="white"
          />
          <MetricCard
            title="Empleados Certificados"
            value={complianceMetrics.certifiedEmployees}
            icon={<PeopleOutline className="text-blue-600" />}
            variant="white"
          />
          <MetricCard
            title="Auditorías Pendientes"
            value={complianceMetrics.pendingAudits.toString()}
            icon={<ScheduleOutlined className="text-gray-600" />}
            variant="white"
          />
        </div>

        <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Pending Actions */}
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <WarningAmberOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Acciones Pendientes</h3>
              </div>

              <div className="space-y-3">
                {pendingActions.map(action => (
                  <div
                    key={action.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{action.title}</p>
                        <p className="text-sm text-gray-500 mt-1">Vencimiento: {action.dueDate}</p>
                        <p className="text-xs text-gray-500">Responsable: {action.responsible}</p>
                      </div>
                      <Tag variant={getPriorityVariant(action.priority)}>{action.priority}</Tag>
                    </div>
                    <div className="mt-3">
                      <Button variant="white" size="small">
                        Ver Detalles
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContainer>

            {/* Certifications and Training */}
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <BarChartOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Certificaciones y Capacitación
                </h3>
              </div>

              {/* Certification Status */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  Estado de Certificaciones
                </h4>
                <div className="space-y-3">
                  {certificationStatuses.map((cert, index) => (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700">{cert.category}</span>
                        <span className="text-sm font-semibold text-green-600">
                          {cert.percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-400 h-2 rounded-full"
                          style={{ width: `${cert.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Trainings */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">
                  Próximas Capacitaciones
                </h4>
                <div className="space-y-3">
                  {upcomingTrainings.map(training => (
                    <div
                      key={training.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <p className="font-medium text-gray-900">{training.title}</p>
                      <p className="text-sm text-gray-500">{training.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContainer>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Company Policies */}
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <DescriptionOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Políticas Empresariales</h3>
              </div>

              <div className="space-y-3">
                {companyPolicies.map(policy => (
                  <div
                    key={policy.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{policy.title}</p>
                      <p className="text-sm text-gray-500">
                        Última actualización: {policy.lastUpdate}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {policy.status === 'completed' ? (
                        <CheckCircleOutlined className="text-green-600" fontSize="small" />
                      ) : (
                        <ScheduleOutlined className="text-yellow-600" fontSize="small" />
                      )}
                      <Button variant="white" size="small" startIcon={<VisibilityOutlined />}>
                        Ver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContainer>

            {/* Quick Actions */}
            <CardContainer>
              <div className="flex items-center gap-2 mb-6">
                <AssessmentOutlined className="text-gray-700" />
                <h3 className="text-lg font-semibold text-gray-900">Acciones Rápidas</h3>
              </div>

              <div className="space-y-3">
                {quickActions.map(action => (
                  <button
                    key={action.id}
                    className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  >
                    <div className="text-gray-600">{getIconComponent(action.icon)}</div>
                    <p className="font-medium text-gray-900">{action.title}</p>
                  </button>
                ))}
              </div>
            </CardContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
